import React, { useState, useMemo, useCallback, useContext } from 'react';
import classNames from 'classnames';
import { Button, Loader } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'components/InfiniteScroll';
import useInfinitePagination from 'hooks/useInfinitePagination';
import { InfiniteScrollRef } from 'components/InfiniteScroll/InfiniteScroll.component';
import ChatMessage from './components/ChatMessage';
import { MessageType, MessageLocation } from 'models/Message';
import { focusElement, setDatesToMessages } from './helpers';
import useChatListener from './hooks/useChatListeners';
import useChatScroll from './hooks/useChatScroll';
import useCallbackRef from 'hooks/useCallbackRef';
import bus, { CallbackBusType, StandardEventTypes } from 'modules/bus';
import uniqBy from 'lodash/uniqBy';
import MessagingContext from '../../providers/Messaging/Messaging.context';
import ArrowleftIcon from 'icons/Arrowleft.icon';
import ArrowrightIcon from 'icons/Arrowright.icon';
import ContentLoader, { IContentLoaderProps } from 'react-content-loader';
import api from 'api';
import {
  getHasElementsInArray,
  getIsArrayEmpty,
  getMemberInitials,
} from 'utils';
import LockclosedIcon from 'icons/Lockclosed.icon';
import { MessageForm } from 'components/MessageForm';
import useUploadMessageFile from 'hooks/useUploadMessageFile';
import MessagesEmptyState from './components/MessagesEmptyState';
import { AxiosRequestConfig } from 'axios';
import useBusEvent from 'hooks/useBusEvent';
import MessagesEmptyImage from 'assets/images/market-a-listing.webp';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import {
  MessageCreatedEventData,
  MessageDeletedEventData,
  MessageSeenEventData,
} from 'services/socketService/socketEventData';
import { trimRichTextValueFromJson } from 'components/RichTextEditorField/utils';
import { AvatarWithInitials } from 'components/AvatarWithInitials';
import { SIZE } from 'models/Size';
import useMessageRepresented from '../../hooks/useMessageRepresented';
import useMarkSessionRead from '../../hooks/useMarkSessionRead';
import WarningBanner from 'components/WarningBanner';
import useClosedChat from '../../hooks/useClosedChat';

import './Chat.styles.scss';
import './Chat.styles.responsive.scss';

export const UserPhotoPlaceholder: React.FC<IContentLoaderProps> = (props) => (
  <ContentLoader
    style={{ width: '120px', height: '69px' }}
    backgroundColor="#e7f1fc"
    foregroundColor="#ffffff"
    preserveAspectRatio="none"
    {...props}
  >
    <circle cx="60" cy="20" r="20" />;
    <rect x="0" y="48" rx="8" ry="8" width="120px" height="16" />
  </ContentLoader>
);

type ChatProps = {
  messageLocation: MessageLocation;
  showUserData?: boolean;
  showButtons?: boolean;
};

const Chat: React.FC<ChatProps> = (props) => {
  const { messageLocation, showUserData = false, showButtons = false } = props;

  const {
    currentSession,
    loadingSession,
    sessionId,
    setContactOpen,
    setContactsOpen,
  } = useContext(MessagingContext);

  const { isDirectMessage, interlocutor } = currentSession || {};

  const classes = classNames('chat');

  const onUploadReq = useCallback(
    (file: File, config: AxiosRequestConfig) =>
      api.messages.uploadFile(sessionId, file, config),
    [sessionId],
  );

  const {
    files,
    uploadedFiles,
    areAllFilesProcessed,
    restRootProps,
    clearFiles,
    onFileRemove,
    onFileUpload,
    getInputProps,
  } = useUploadMessageFile({
    onUploadReq,
  });

  const [hasNewMessage, setHasNewMessage] = useState<boolean>(false);

  const [messagesContainer, messagesContainerRef] =
    useCallbackRef<InfiniteScrollRef>();

  const { forbidMessaging, bannerText, forbidMessagingText } =
    useClosedChat(currentSession);

  const { dealName, dealLogo, representerName, representerLogo } =
    useMessageRepresented()(currentSession);

  const markSessionRead = useMarkSessionRead();

  const {
    loading,
    items: messages,
    setItems: setMessages,
    onContainerScrolled: onMessagesScroll,
  } = useInfinitePagination<MessageType>({
    condition: !!sessionId,
    resetItemsWhenReset: false,
    resetDeps: [sessionId],
    dependencies: [sessionId],
    makeRequest: async (currentPage: number) => {
      return api.messages
        .getSessionMessages(
          {
            $page: currentPage.toString(),
            $limit: '50',
            $sort: '-createdAt',
          },
          sessionId,
        )
        .then((res) => res.data);
    },
    onItemsLoad: (cp) => {
      // when it is scrollable and current page not first one
      // then scroll to bottom to see loaded messages
      if (shouldScrollTL && cp === 1) scrollToView();
    },
  });

  const finalMessages = useMemo(
    () =>
      setDatesToMessages(messages).filter((msg) => msg.sessionId === sessionId),
    [messages, sessionId],
  );

  const { shouldScrollTL, scrollToBottom } = useChatScroll({
    condition: hasNewMessage,
    container: messagesContainer?.container as HTMLDivElement,
  });

  const isSessionReady = !loading && currentSession?._id === sessionId;

  const loadingSpinner = useMemo(
    () => (isSessionReady ? null : <Loader overlay={false} />),
    [isSessionReady],
  );

  const onMessageArrived = useCallback(
    ({ message, session }: MessageCreatedEventData) => {
      if (sessionId !== message.sessionId) return;

      // if user scrolled container above last message
      // and new message arrived then set flag for showSTL(show scroll to latest)
      if (shouldScrollTL) {
        setHasNewMessage(true);
        markSessionRead(sessionId, session.isExternalCommunication);
      }
    },
    [sessionId, shouldScrollTL, markSessionRead],
  );

  useChatListener({
    setMessages,
    onMessageArrived,
  });

  const scrollToView = useCallback(() => {
    setHasNewMessage(false);
    scrollToBottom();
  }, [scrollToBottom]);

  const { t } = useTranslation();

  const handleOnSendMessage = useCallback(
    async (message: string) => {
      const trimmedMessage = trimRichTextValueFromJson(message);

      const { data: sentMessage } = await api.messages.sendMesssage(
        sessionId,
        trimmedMessage,
        uploadedFiles,
        messageLocation,
      );

      // dedup in case the socket echo of this message already arrived
      setMessages((oldMessages) =>
        uniqBy([sentMessage, ...oldMessages], '_id'),
      );

      const { createdChatSessionFiles } = sentMessage;

      clearFiles();

      // event message that has been sent in current session
      if (shouldScrollTL) scrollToView();
      bus.broadcastEvent(StandardEventTypes.MESSAGE_SENT, sentMessage);

      if (getHasElementsInArray(createdChatSessionFiles))
        bus.broadcastEvent(
          StandardEventTypes.FILES_SENT,
          createdChatSessionFiles,
        );

      focusElement('#message-form-input');
    },
    [
      sessionId,
      uploadedFiles,
      messageLocation,
      setMessages,
      clearFiles,
      shouldScrollTL,
      scrollToView,
    ],
  );

  const onFileDelete = useCallback(
    (data: CallbackBusType<string>) => {
      const { payload } = data;

      setMessages((old) =>
        old.map((el) => ({
          ...el,
          files: el.files.filter(
            ({ chatSessionFileId }) => chatSessionFileId !== payload,
          ),
        })),
      );
    },
    [setMessages],
  );

  useBusEvent(StandardEventTypes.FILE_DELETE, onFileDelete);

  const updateDeletedMessage = useCallback(
    (messageId: string) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m._id !== messageId) return m;

          m.files.forEach((file) =>
            bus.broadcastEvent(StandardEventTypes.FILE_DELETED_WITH_MESSAGE, {
              chatSessionFileId: file.chatSessionFileId,
            }),
          );

          return { ...m, isDeleted: true, text: '', files: [] };
        }),
      );
    },
    [setMessages],
  );

  const onDeleteMessageRequest = useCallback(
    async (messageId: string) => {
      api.messages.deleteMessage(messageId);
      updateDeletedMessage(messageId);
    },
    [updateDeletedMessage],
  );

  const onDeleteMessageEvent = useCallback(
    ({ sessionId: payloadSessionId, messageId }: MessageDeletedEventData) => {
      if (sessionId !== payloadSessionId) return;

      updateDeletedMessage(messageId);
    },
    [sessionId, updateDeletedMessage],
  );

  useSocketEvent(LISTEN_EVENTS.MESSAGE_DELETED, onDeleteMessageEvent);

  const onSeenMessageEvent = useCallback(
    ({
      sessionId: payloadSessionId,
      messageId,
      seenBy,
    }: MessageSeenEventData) => {
      if (sessionId !== payloadSessionId) return;

      setMessages((prev) =>
        prev.map((m) => (m._id === messageId ? { ...m, seenBy } : m)),
      );
    },
    [sessionId, setMessages],
  );

  const updateMessage = useCallback(
    (message: Partial<MessageType>) =>
      setMessages((prev) =>
        prev.map((m) => (message._id === m._id ? { ...m, ...message } : m)),
      ),
    [setMessages],
  );

  useSocketEvent(LISTEN_EVENTS.MESSAGE_SEEN, onSeenMessageEvent);

  return (
    <div className={classes} {...restRootProps}>
      <input {...getInputProps()} />
      {showUserData && (
        <div className="chat__user-data">
          {showButtons && (
            <Button
              variant="icon"
              className="chat-arrow"
              icon={ArrowleftIcon}
              onClick={() => setContactsOpen((old) => !old)}
              aria-label="Show all chats."
            />
          )}

          <div className="chat__user-data__info">
            {loadingSession || !currentSession ? (
              <UserPhotoPlaceholder />
            ) : (
              <>
                <div className="chat__user-data__image">
                  <AvatarWithInitials
                    size={SIZE.BIG}
                    {...(isDirectMessage
                      ? { user: interlocutor }
                      : {
                          initials: getMemberInitials(
                            representerName || dealName,
                          ),
                          imageUrl: representerLogo || dealLogo,
                        })}
                  />
                </div>
                <p className="chat__users-data__name">
                  {representerName || dealName}
                </p>
              </>
            )}
          </div>

          {showButtons && (
            <Button
              variant="icon"
              className="chat-arrow"
              icon={ArrowrightIcon}
              onClick={() => setContactOpen((old) => !old)}
              aria-label="Show contact details."
            />
          )}
        </div>
      )}

      {bannerText && (
        <WarningBanner title={bannerText} icon={<LockclosedIcon />} />
      )}

      <InfiniteScroll
        ref={messagesContainerRef}
        className="chat__messages"
        localScroll
        onScroll={onMessagesScroll}
      >
        {isSessionReady ? (
          getIsArrayEmpty(finalMessages) ? (
            <MessagesEmptyState
              text={t('emptyStateChat', {
                name: representerName,
              })}
              description={t('sendFirstMessage')}
              imageSrc={MessagesEmptyImage}
            />
          ) : (
            finalMessages.map(({ _id, ...message }, index) => (
              <ChatMessage
                key={_id}
                messageId={_id}
                disabled={forbidMessaging}
                prevMessage={index > 0 ? finalMessages[index - 1] : null}
                onDeleteMessageRequest={onDeleteMessageRequest}
                updateMessage={updateMessage}
                {...message}
              />
            ))
          )
        ) : null}

        {loadingSpinner}
      </InfiniteScroll>

      <MessageForm
        disabled={forbidMessaging}
        placeholder={forbidMessagingText || t('writeAMessage')}
        resetMessage={sessionId}
        uploadFileHintMessage={t('hintYouCanDragFilesToChat')}
        buttonText={t('send')}
        files={files}
        areAllFilesProcessed={areAllFilesProcessed}
        onFileUpload={onFileUpload}
        onFileRemove={onFileRemove}
        handleOnSubmit={handleOnSendMessage}
      />
    </div>
  );
};

export default Chat;
